import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from 'next-localization';
import { useRef } from 'react';
import debounce from 'lodash.debounce';
import classNames from 'classnames';
import StateDrivenDropdown from 'components/Generic/StateDrivenDropdown';
import styles from './styles/SecondLevelTabs.module.scss';

type Props = {
  tabs: Array<{ key: string; title: string }>;
  activeTabKey: string;
  tabClick: (tabKey: string) => void;
};

const SecondLevelTabs = ({ tabs, activeTabKey = '', tabClick }: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [sortedTabs, setSortedTabs] = useState([...tabs]);
  const [visibleTabs, setVisibleTabs] = useState([]);
  const [hiddenTabs, setHiddenTabs] = useState<Array<{ key: string; title: string }>>([]);

  const { t } = useI18n();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const visibleTabsWrapperRef = useRef<HTMLDivElement | null>(null);

  const updateDropdown = useCallback(() => {
    setVisibleTabs([...sortedTabs]);
    setHiddenTabs([]);

    // Run calculations after the DOM has been updated
    setTimeout(() => {
      const wrapper = wrapperRef.current;
      const visibleTabsWrapper = visibleTabsWrapperRef.current;

      if (visibleTabsWrapper?.scrollWidth > wrapper?.offsetWidth) {
        // Calculate the tab index where overflow begins
        let index = Array.from(visibleTabsWrapper?.children || []).findIndex((tab) => {
          const tabOffsetRight = tab.offsetLeft + tab.offsetWidth;
          const visibleTabsWrapperOffsetRight =
            visibleTabsWrapper.offsetLeft + visibleTabsWrapper.offsetWidth;
          return tabOffsetRight > visibleTabsWrapperOffsetRight;
        });

        index = Math.max(index - 1, 1);

        const newVisibleTabs = sortedTabs.slice(0, index);
        const newHiddenTabs = sortedTabs.slice(index);

        setVisibleTabs(newVisibleTabs);
        setHiddenTabs(newHiddenTabs);

        // Check if activeTabKey is in hiddenTabs
        if (newHiddenTabs.find((tab) => tab.key === activeTabKey)) {
          const activeTabIndex = tabs.findIndex((tab) => tab.key === activeTabKey);
          const newTabs = [...tabs];
          const [activeTab] = newTabs.splice(activeTabIndex, 1);

          // Move active tab to the front
          newTabs.unshift(activeTab);
          setSortedTabs(newTabs);
        }
      }
    }, 0);
  }, [sortedTabs, tabs, activeTabKey]);

  const onResize = useMemo(() => {
    return debounce(updateDropdown, 500);
  }, [updateDropdown]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const openDropdown = () => {
    setIsDropdownOpen(true);
  };
  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const onDropdownTabButtonClick = (tabKey: string) => {
    tabClick(tabKey);
    closeDropdown();
  };

  useEffect(() => {
    updateDropdown();
  }, [tabs, activeTabKey, sortedTabs, updateDropdown]);

  useEffect(() => {
    updateDropdown();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  return (
    <section>
      <div className="container">
        <div ref={wrapperRef} className="d-flex justify-content-between gap-4.5">
          <div ref={visibleTabsWrapperRef} className="d-flex gap-4.5 flex-grow-1 overflow-hidden">
            {visibleTabs.map((tab: { key: string; title: string }) => (
              <button
                key={tab.key}
                className={classNames('text-nowrap', {
                  [styles['tab-button--active']]: tab.key === activeTabKey,
                  [styles['tab-button--only']]: visibleTabs.length === 1,
                  [styles['tab-button']]: true,
                })}
                onClick={() => tabClick(tab.key)}
              >
                {tab.title}
              </button>
            ))}
          </div>

          {hiddenTabs.length !== 0 && (
            <StateDrivenDropdown
              isOpen={isDropdownOpen}
              className={`dropdown dropdown--content-right ${
                isDropdownOpen ? 'dropdown--expanded' : ''
              }`}
              buttonsLayout="justify-content-between"
              onOutsideClick={closeDropdown}
              triggerAreaSlot={
                <button
                  className={`${styles.dropdown__button} d-flex align-items-center gap-1`}
                  onClick={toggleDropdown}
                >
                  <span className="text-link text-nowrap">{t('viewMore')}</span>
                  <i className="bi bi-chevron-down icon--chevron" />
                </button>
              }
            >
              <div className="dropdown__panel py-1">
                {hiddenTabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`${styles.dropdown__tabButton} p-2 button-label text-nowrap`}
                    onClick={() => onDropdownTabButtonClick(tab.key)}
                    onFocus={openDropdown}
                    onBlur={closeDropdown}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>
            </StateDrivenDropdown>
          )}
        </div>
        <div className={styles.divider} />
      </div>
    </section>
  );
};

export default SecondLevelTabs;
